import {
  IMenhirInsuranceCoveredMember,
  IMenhirInsuranceMember,
  IMenhirInsuranceNotCoveredMember,
  IMenhirInsurancePayrollMovement,
  IMenhirInsurancePolicies,
  IMenhirInsurancePolicy,
  IMenhirInsuranceProduct,
  TInsuranceAvailablePlan,
  TInsurancePlanSimulation,
  TInsurancePolicyOperator,
  TMemberEligibility,
  TMemberSimulation,
  TMenhirInsuranceAvailablePlan,
  TMenhirInsuranceCoverage,
  TMenhirInsuranceDuplicatedMember,
  TMenhirInsurancePerson,
  TMenhirInsurancePlanEligibility,
  TMenhirInsurancePlanSimulation,
  TMenhirInsurancePolicyOperator,
  TMenhirInsuranceUpgradePlan,
  TMenhirMemberEligibility,
  TMenhirMemberSimulation,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import {
  IInsuranceCoveredMember,
  IInsuranceMember,
  IInsuranceNotCoveredMember,
  IInsurancePayrollMovement,
  IInsurancePolicies,
  IInsurancePolicy,
  IInsuranceProduct,
  TInsuranceCoverage,
  TInsuranceDuplicatedMember,
  TInsurancePerson,
  TInsuranceUpgradePlan,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { DEFAULT_HOLDER_PAYMENT_METHODS_ALLOWED } from './insurance.constants';

export const transformInsurancePerson = (
  person: TMenhirInsurancePerson
): TInsurancePerson => ({
  id: person.id,
  shortName: person.short_name,
  paidByCompany: person.paid_by_company,
  paidByEmployee: person.paid_by_employee,
  included: Boolean(person.included),
  type: person.type,
});

export const transformInsuranceDuplicatedFamilyMember = (
  familyMember: TMenhirInsuranceDuplicatedMember
): TInsuranceDuplicatedMember => ({
  id: familyMember.id,
  taxId: familyMember.tax_id,
  shortName: familyMember.short_name,
  alreadyInsured: familyMember.already_insured,
});

export const transformInsuranceCoverage = (
  coverage: TMenhirInsuranceCoverage
): TInsuranceCoverage => ({
  name: coverage.name,
  info: coverage.info,
  value: coverage.value,
  comparable: Boolean(coverage.comparable),
});

const transformInsuranceMember = (
  member: IMenhirInsuranceMember
): IInsuranceMember => ({
  id: member.id,
  name: member.name,
  shortName: member.short_name,
  createdAt: member.created_at,
  startsOn: member.starts_on,
  status: member.status,
  type: member.type,
  cost: calculateMemberCost(member),
  metadata: transformInsurancePolicyMetadata(member.metadata),
  paymentType: member.payment_type,
  endsOn: member.ends_on,
  optOutEligible: member.opt_out_eligible,
  cardNumber: member.card_number,
});

const calculateMemberCost = (
  member: IMenhirInsuranceMember
): IInsuranceMember['cost'] => {
  if (!member.cost) return undefined;

  return {
    premium: member.cost.premium,
    percentage: member.cost.percentage,
    paidByCompany: member.cost.paid_by_company,
    paidByEmployee: member.cost.paid_by_employee,
  };
};

const transformInsuranceProduct = (
  product: IMenhirInsuranceProduct
): IInsuranceProduct => ({
  plan: {
    id: product.plan.id,
    coverages: product.plan.coverages,
    name: product.plan.name,
    type: product.plan.type,
    description: product.plan.description,
    gracePeriod: product.plan.grace_period,
  },
  provider: {
    id: product.provider.id,
    name: product.provider.name,
    slug: product.provider.slug,
    logoUrl: product.provider.logo_url,
  },
});

export const transformInsurancePolicy = (
  policy: IMenhirInsurancePolicy
): IInsurancePolicy => ({
  id: policy.id,
  status: policy.status,
  policyNumber: policy.policy_number,
  familyMembersAllowed: policy.family_members_allowed,
  canAddFamilyMembers: policy.can_add_family_members,
  optOutAllowed: policy.opt_out_allowed,
  price: policy.price,
  totalCost: getInsurancePolicyTotalCost(policy),
  persons: getInsurancePolicyPersons(policy),
  startsOn: policy.starts_on,
  renewsOn: policy.renews_on,
  settings: policy.settings,
  product: transformInsuranceProduct(policy.product),
  metadata: transformInsurancePolicyMetadata(policy.metadata),
  documents: policy.documents,
  gracePeriod: policy.grace_period,
  upgradable: policy.upgradable,
  holderPaymentMethodsAllowed:
    policy.holder_payment_methods_allowed ??
    DEFAULT_HOLDER_PAYMENT_METHODS_ALLOWED,
});

export const transformInsurancePolicyMetadata = (
  metadata: IMenhirInsurancePolicy['metadata']
): IInsurancePolicy['metadata'] => {
  return (
    metadata && {
      nextPaymentOn: metadata.next_payment_on,
      exemptionAmount: metadata.exemption_amount,
      nonExemptAmount: metadata.non_exempt_amount,
    }
  );
};

const getInsurancePolicyTotalCost = (policy: IMenhirInsurancePolicy) => {
  return {
    premium: policy.total_cost.premium,
    paidByCompany: policy.total_cost.paid_by_company,
    paidByEmployee: policy.total_cost.paid_by_employee,
  };
};

const getInsurancePolicyPersons = (
  policy: IMenhirInsurancePolicy
): IInsuranceMember[] => {
  return policy.persons.map(transformInsuranceMember);
};

const transformInsurancePoliciesList = (
  policies: IMenhirInsurancePolicy[]
): IInsurancePolicy[] => policies.map(transformInsurancePolicy);

export const transformNotCoveredMember = (
  member: IMenhirInsuranceNotCoveredMember
): IInsuranceNotCoveredMember => ({
  id: member.id,
  birthDate: member.birth_date,
  taxId: member.tax_id,
  gender: member.gender,
  cost: {
    premium: member.cost.premium,
    paidByEmployee: member.cost.paid_by_employee
      ? member.cost.paid_by_employee
      : /* Backwards compatibility for when paid_by_employee doesn't exist */
        {
          ...member.cost.premium,
          amount: member.cost.premium.amount * (1 - member.cost.percentage),
        },
    paidByCompany: member.cost.paid_by_company
      ? member.cost.paid_by_company
      : /* Backwards compatibility for when paid_by_employee doesn't exist */
        {
          ...member.cost.premium,
          amount: member.cost.premium.amount * member.cost.percentage,
        },
  },
  metadata: transformInsurancePolicyMetadata(member.metadata),
  name: member.name,
  shortName: member.short_name,
  type: member.type,
});

export const transformInsuranceNotCoveredMembers = (
  members: IMenhirInsuranceNotCoveredMember[]
): IInsuranceNotCoveredMember[] => members.map(transformNotCoveredMember);

export const transformInsuranceCoveredMember = (
  member: IMenhirInsuranceCoveredMember
): IInsuranceCoveredMember => ({
  id: member.id,
  cost: member.cost
    ? {
        premium: member.cost.premium,
        paidByCompany: member.cost.paid_by_company,
        paidByEmployee: member.cost.paid_by_employee,
        percentage: member.cost.percentage,
      }
    : undefined,
  startsOn: member.starts_on,
  name: member.name,
  shortName: member.short_name,
  paymentType: member.payment_type,
  status: member.status,
  type: member.type,
});

const transformInsurancePayrollMovement = (
  movement: IMenhirInsurancePayrollMovement
): IInsurancePayrollMovement => ({
  id: movement.id,
  paidAt: movement.paid_at,
  amount: movement.amount,
  coveredPerson: transformInsuranceCoveredMember(movement.covered_person),
});

export const transformPayrollMovements = (
  movements: IMenhirInsurancePayrollMovement[]
): IInsurancePayrollMovement[] =>
  movements.map(transformInsurancePayrollMovement);

export const transformInsurancePolicies = (
  policies: IMenhirInsurancePolicies
): IInsurancePolicies => ({
  activePolicies: transformInsurancePoliciesList(policies.insurances.list),
  upcomingPolicy: policies.upcoming
    ? transformInsurancePolicy(policies.upcoming)
    : undefined,
});

export const transformInsuranceUpgradePlan = (
  plan: TMenhirInsuranceUpgradePlan
): TInsuranceUpgradePlan => ({
  policyId: plan.policy_id,
  name: plan.name,
  selected: plan.selected,
  startDate: plan.start_date,
  familyMembersAllowed: plan.family_members_allowed,
  canAddFamilyMembers: plan.can_add_family_members,
  paidByCompany: {
    employee: plan.paid_by_company.employee,
    spouse: plan.paid_by_company.spouse,
    child: plan.paid_by_company.child,
  },
  coverages: plan.coverages.map(transformInsuranceCoverage),
  persons: plan.persons.map(transformInsurancePerson),
  files: plan.files,
  holderPaymentMethodsAllowed:
    plan.holder_payment_methods_allowed ??
    DEFAULT_HOLDER_PAYMENT_METHODS_ALLOWED,
});

export const transformInsuranceAvailablePlan = (
  plan: TMenhirInsuranceAvailablePlan
): TInsuranceAvailablePlan => ({
  policyId: plan.policy_id,
  name: plan.name,
  selected: plan.selected,
  metadata: plan.metadata
    ? {
        exemptionAmount: plan.metadata.exemption_amount,
      }
    : undefined,
  startDate: plan.start_date,
  familyMembersAllowed: plan.family_members_allowed,
  canAddFamilyMembers: plan.can_add_family_members,
  paidByCompany: {
    employee: plan.paid_by_company.employee,
    spouse: plan.paid_by_company.spouse,
    child: plan.paid_by_company.child,
  },
  coverages: plan.coverages.map(transformInsuranceCoverage),
  persons: plan.persons.map(transformInsurancePerson),
  files: plan.files,
  holderPaymentMethodsAllowed:
    plan.holder_payment_methods_allowed ??
    DEFAULT_HOLDER_PAYMENT_METHODS_ALLOWED,
});

export const transformPlanSimulationPerson = (
  person: TMenhirMemberSimulation
): TMemberSimulation => ({
  id: person.id,
  type: person.type,
  fullName: person.full_name,
  shortName: person.short_name,
  taxNumber: person.tax_id,
  gender: person.gender,
  birthdate: person.birth_date,
  paidByEmployee: person.paid_by_employee,
  paidByCompany: person.paid_by_company,
  selected: person.selected,
  error: person.error,
  isUserAdded: person.is_user_added,
  editableFields: person.editable_fields ?? [],
});

export const transformPlanSimulation = (
  simulation: TMenhirInsurancePlanSimulation
): TInsurancePlanSimulation => ({
  persons: simulation.persons.map(transformPlanSimulationPerson),
  totalCost: simulation.total_cost,
  metadata: simulation.metadata
    ? {
        exemptionAmount: simulation.metadata.exemption_amount,
      }
    : undefined,
  savingsAmount: simulation.savings_amount,
  holderPaymentMethodsAllowed: simulation.holder_payment_methods_allowed,
});

export const transformPlanEligibility = (
  simulation: TMenhirInsurancePlanEligibility
): TMemberEligibility[] =>
  simulation.persons.map(transformPlanEligibiltyPerson);

export const transformPlanEligibiltyPerson = (
  person: TMenhirMemberEligibility
): TMemberEligibility => ({
  type: person.type,
  fullName: person.full_name,
  shortName: person.short_name,
  taxNumber: person.tax_id,
  gender: person.gender,
  birthdate: person.birth_date,
  error: person.error,
  selected: person.selected,
  isUserAdded: person.is_user_added,
  editableFields: person.editable_fields ?? [],
});

export const transformMenhirPlanSimulationPerson = (
  person: TMemberSimulation
): Omit<TMenhirMemberSimulation, 'paid_by_employee' | 'paid_by_company'> => ({
  id: person.id,
  type: person.type,
  full_name: person.fullName,
  short_name: person.shortName,
  tax_id: person.taxNumber,
  gender: person.gender,
  birth_date: person.birthdate,
  selected: person.selected,
  error: person.error,
  is_user_added: person.isUserAdded,
  editable_fields: person.editableFields,
});

export const transformInsurancePolicyOperator = (
  operator: TMenhirInsurancePolicyOperator
): TInsurancePolicyOperator => ({
  name: operator.name,
  phone: operator.phone,
  email: operator.email,
  url: operator.url,
  logoUrl: operator.logo_url,
});
